import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QFormControl, QInput, QSelect, QStack } from '@qualio/ui-components';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { EventTemplateFormValues, trimAndConvertToNumber, User } from '../../types';
import * as DisplayStrings from '../../displayStrings';

type TemplateDetailsFormProps = {
  formMethods: UseFormReturn<EventTemplateFormValues>;
  possibleOwners: readonly User[];
  isTemplateReadonly: boolean;
};

const TemplateDetailsForm = ({ formMethods, possibleOwners, isTemplateReadonly }: TemplateDetailsFormProps) => {
  const { t } = useTranslation();
  const {
    control,
    trigger,
    formState: { errors },
  } = formMethods;
  const defaultOwnerOptions = useMemo(
    () => possibleOwners.map((owner) => ({ value: `${owner.id}`, label: owner.full_name })),
    [possibleOwners],
  );

  return (
    <FormProvider {...formMethods}>
      <QStack spacing={4}>
        <QFormControl
          label={t('common.labels.title')}
          error={errors.name && errors.name.message}
          isInvalid={!!errors.name}
          isReadOnly={isTemplateReadonly}
          isRequired
        >
          <Controller
            control={control}
            name="name"
            render={({ field: { ref, ...field } }) => (
              <QInput
                {...field}
                onKeyUp={async () => {
                  await trigger('name');
                }}
                data-cy="Name"
              />
            )}
          />
        </QFormControl>

        <QFormControl
          label={t('common.labels.prefix')}
          error={errors.prefix && errors.prefix.message}
          isInvalid={!!errors.prefix}
          isReadOnly={true}
          helper={t('templates.messages.aPrefixWillBeAutomaticallyAdded')}
          isRequired={true}
        >
          <Controller
            control={control}
            name="prefix"
            render={({ field: { ref, ...field } }) => (
              <QInput
                {...field}
                onKeyUp={async () => {
                  await trigger('prefix');
                }}
                data-cy="Prefix"
              />
            )}
          />
        </QFormControl>

        <Controller
          control={control}
          name="default_owner_id"
          render={({ field: { onChange, ref, ...field }, fieldState }) => {
            return (
              <QFormControl
                label={DisplayStrings.DefaultAssignee}
                isInvalid={fieldState.invalid}
                error={fieldState.error?.message}
                helper={DisplayStrings.DefaultAssigneeHelper}
              >
                <QSelect
                  {...field}
                  data-cy="DefaultAssignee"
                  options={defaultOwnerOptions}
                  onChange={(changeObject) => {
                    onChange(changeObject ? changeObject.value : '');
                  }}
                  isClearable={true}
                  isDisabled={isTemplateReadonly}
                />
              </QFormControl>
            );
          }}
        />

        <QFormControl
          label={t('common.labels.timeLimitDays')}
          error={errors.default_time_limit && errors.default_time_limit.message}
          isInvalid={!!errors.default_time_limit}
          isReadOnly={isTemplateReadonly}
          helper={t('templates.messages.eachEventMustBeResolved')}
          isRequired
        >
          <Controller
            control={control}
            name="default_time_limit"
            render={({ field: { ref, ...field } }) => (
              <QInput
                {...field}
                // Needs to be here so that react hook form can properly tell if the field is dirty
                onChange={(e) => {
                  const value = e.target.value;

                  field.onChange(trimAndConvertToNumber(value));
                }}
                onKeyUp={async () => {
                  await trigger('default_time_limit');
                }}
                type="number"
                data-cy="DefaultTimeLimit"
              />
            )}
          />
        </QFormControl>

        <QFormControl
          label={t('common.labels.validationStepTimeDelayDays')}
          error={errors.start_time_constraint && errors.start_time_constraint.message}
          isInvalid={!!errors.start_time_constraint}
          isReadOnly={isTemplateReadonly}
          helper={t('templates.messages.determinesHowManyDays')}
          isRequired
        >
          <Controller
            control={control}
            name="start_time_constraint"
            render={({ field: { ref, ...field } }) => (
              <QInput
                {...field}
                // Needs to be here so that react hook form can properly tell if the field is dirty
                onChange={(e) => {
                  const value = e.target.value;

                  field.onChange(trimAndConvertToNumber(value));
                }}
                onKeyUp={async () => {
                  await trigger('start_time_constraint');
                }}
                type="number"
                data-cy="StartTimeConstraint"
              />
            )}
          />
        </QFormControl>
      </QStack>
    </FormProvider>
  );
};

export default TemplateDetailsForm;
